import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LANGUAGES, langLabel } from "../lib/languages";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import MoodChart from "../components/MoodChart";
import ExportPDF from "../components/ExportPDF";
import { PatientSessionsWorkspace } from "../components/sessions/PatientSessionsWorkspace";
import ClinicalSynthesis from "../components/ClinicalSynthesis";
import { CouplePartnerBadge } from "../components/CouplePartnerBadge";
import { InvitePatientButton } from "../components/InvitePatientButton";
import PatientAnamnesis from "../components/PatientAnamnesis";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Languages, Heart, Brain, BookOpen, TrendingUp, User, BarChart3, Star, Leaf, FileText, Download, ClipboardList, Pencil, Save, X, ChevronDown, ChevronUp, Calendar, Phone, MapPin, Trophy, Plus, FolderOpen, Mic , FileDown, Wand2, Merge, Send } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { WorksheetPersonalizeDialog } from "../components/WorksheetPersonalizeDialog";
import { MergePatientDialog } from "../components/MergePatientDialog";
import { downloadWorksheetPdf } from "../components/WorksheetEditorDialog";
import SentDocumentsPanel from "../components/SentDocumentsPanel";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const PatientProgressPage = ({ user, logout, embedded = false, patientIdProp = null, onPatientChanged }) => {
  const { t, locale } = useI18n();
  const { patientId: routePatientId } = useParams();
  const patientId = patientIdProp || routePatientId;
  const [, setSearchParams] = useSearchParams();
  const wrap = (node) => (embedded ? node : <DashboardLayout user={user} logout={logout}>{node}</DashboardLayout>);
  const [patient, setPatient] = useState(null);
  const [allPatients, setAllPatients] = useState([]);
  const [activeTab, setActiveTab] = useState(() => (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("session")) ? "reports" : "mood");
  const [moodEntries, setMoodEntries] = useState([]);
  const [moodAnalytics, setMoodAnalytics] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionReports, setSessionReports] = useState([]);
  const [personalize, setPersonalize] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [customSheets, setCustomSheets] = useState([]);
  const loadCustomSheets = () => axios.get(`${API}/exercise-worksheets/custom/by-patient/${patientId}`, { withCredentials: true }).then((r) => setCustomSheets((r.data.custom || []).map((c) => c.type_id))).catch(() => {});
  useEffect(() => { if (patientId) loadCustomSheets(); }, [patientId]);
  const [savingReport, setSavingReport] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [badgeData, setBadgeData] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    const onSynced = () => axios.get(`${API}/session-reports/patient/${patientId}`, { withCredentials: true }).then((r) => setSessionReports(r.data)).catch(() => {});
    window.addEventListener("kintsugi:calendly-synced", onSynced);
    return () => window.removeEventListener("kintsugi:calendly-synced", onSynced);
  }, [patientId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patientsRes, moodRes, assignmentsRes, analyticsRes, exportRes, reportsRes] = await Promise.all([
          axios.get(`${API}/therapist/patients`, { withCredentials: true }),
          axios.get(`${API}/mood/patient/${patientId}`, { withCredentials: true }),
          axios.get(`${API}/exercises/assignments`, { withCredentials: true }),
          axios.get(`${API}/mood/analytics/patient/${patientId}`, { withCredentials: true }),
          axios.get(`${API}/patient/export/${patientId}`, { withCredentials: true }),
          axios.get(`${API}/session-reports/patient/${patientId}`, { withCredentials: true }),
        ]);
        const foundPatient = patientsRes.data.find(p => p.user_id === patientId);
        setPatient(foundPatient);
        setAllPatients(patientsRes.data);
        if (foundPatient) {
          setProfileForm({
            first_name: foundPatient.first_name || "",
            last_name: foundPatient.last_name || "",
            name: foundPatient.name || "",
            date_of_birth: foundPatient.date_of_birth || "",
            gender: foundPatient.gender || "",
            phone: foundPatient.phone || "",
            address: foundPatient.address || "",
            parent_emails: foundPatient.parent_emails || "",
            language: foundPatient.language || "fr",
            notes: foundPatient.notes || "",
          });
        }
        setMoodEntries(moodRes.data);
        setAssignments(assignmentsRes.data.filter(a => a.patient_id === patientId));
        setMoodAnalytics(analyticsRes.data);
        setExportData(exportRes.data);
        setSessionReports(reportsRes.data);

        // Fetch patient badges
        try {
          const badgeRes = await axios.get(`${API}/badges/patient/${patientId}`, { withCredentials: true });
          setBadgeData(badgeRes.data);
        } catch (e) { /* Badges not available */ }
      } catch (error) { console.error("Error fetching data:", error); } finally { setLoading(false); }
    };
    fetchData();
  }, [patientId]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/therapist/patients/${patientId}/profile`, profileForm, { withCredentials: true });
      setPatient(prev => ({ ...prev, ...res.data }));
      onPatientChanged?.();
      toast.success("Fiche patient mise a jour");
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
    } finally { setSavingProfile(false); }
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : locale === "es" ? "es-ES" : "en-US", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    }).format(date);
  };

  const getMoodColor = (level) => {
    if (level <= 3) return "text-rose-500";
    if (level <= 5) return "text-amber-500";
    if (level <= 7) return "text-lime-500";
    return "text-emerald-500";
  };

  const completedAssignments = assignments.filter(a => a.status === "completed").length;
  const avgMood = moodEntries.length > 0
    ? (moodEntries.reduce((acc, e) => acc + e.mood_level, 0) / moodEntries.length).toFixed(1)
    : "-";



  if (loading) {
    return wrap(
      <div className="space-y-4">
        <div className="animate-pulse h-12 bg-muted rounded-lg w-64" />
        <div className="animate-pulse h-64 bg-muted rounded-lg" />
      </div>
    );
  }

  if (!patient) {
    return wrap(
      <div className="text-center py-12">
        <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t("progress.notFound")}</p>
      </div>
    );
  }

  return wrap(
      <div className="space-y-8" data-testid="patient-progress-page" ref={printRef}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-2xl font-semibold text-primary">{patient.name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold truncate">{patient.name}</h1>
              <p className="text-muted-foreground truncate">{patient.email || <span className="italic">Sans e-mail</span>}</p>
              <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground mt-1" title="Langue du patient : documents, e-mails et générations IA sont rédigés dans cette langue">
                <Languages className="w-3.5 h-3.5" />
                <select value={patient.language || "fr"} onChange={async (e) => { const language = e.target.value; try { await axios.put(`${API}/therapist/patients/language`, { patient_id: patientId, language }, { withCredentials: true }); setPatient({ ...patient, language }); toast.success(`Langue du patient : ${langLabel(language)}`); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } }} className="h-7 rounded-md border border-border bg-background px-1.5 text-xs" data-testid="patient-language-select">
                  {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </label>
              <CouplePartnerBadge patient={patient} patients={allPatients} onChanged={async () => {
                const r = await axios.get(`${API}/therapist/patients`, { withCredentials: true });
                setAllPatients(r.data); setPatient(r.data.find((p) => p.user_id === patientId)); onPatientChanged?.();
              }} />
            </div>
          </div>
          <ExportPDF title={`${t("progress.title")} - ${patient.name}`} contentRef={printRef} />
          <div className="flex items-center gap-2 flex-wrap">
            <InvitePatientButton patient={patient} onInvited={(r) => { setPatient((p) => ({ ...p, invitation_sent_at: r.invitation_sent_at })); onPatientChanged?.(); }} />
            <Link to={`/therapist/patients/${patientId}/exercises`}>
              <Button variant="outline" size="sm" className="gap-2" data-testid="view-exercises-btn">
                <FileText className="w-4 h-4" /> {t("progress.viewExercises")}
              </Button>
            </Link>
            <Link to={`/therapist/patients/${patientId}/ders`}>
              <Button variant="outline" size="sm" className="gap-2" data-testid="view-ders-btn">
                <ClipboardList className="w-4 h-4" /> DERS
              </Button>
            </Link>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setMergeOpen(true)} title="Fusionner un compte en doublon (ex. adresse Calendly différente du compte Kintsugi)" data-testid="merge-patient-btn">
              <Merge className="w-4 h-4" /> Fusionner un doublon
            </Button>
            <Link to={`/therapist/patients/${patientId}/perfectionism`}>
              <Button variant="outline" size="sm" className="gap-2" data-testid="view-perfectionism-btn" title="Questionnaire perfectionnisme & excellencisme : résultats et évolution">
                <ClipboardList className="w-4 h-4" /> Perfectionnisme
              </Button>
            </Link>
            <Link to={`/therapist/patients/${patientId}/grief`}>
              <Button variant="outline" size="sm" className="gap-2" data-testid="view-grief-btn" title="Échelle de deuil : résultats et évolution">
                <ClipboardList className="w-4 h-4" /> Deuil
              </Button>
            </Link>
            <Button
              variant="default" size="sm" className="gap-2"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("session_token");
                  const res = await axios.get(`${API}/export/pdf/patient/${patientId}?days=90`, {
                    headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
                  });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  link.download = `rapport_${patient.name?.replace(/\s/g, "_")}.pdf`;
                  link.click();
                } catch (e) { console.error(e); }
              }}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" /> PDF
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t("progress.averageMood")}</p><p className={`text-3xl font-semibold ${getMoodColor(parseFloat(avgMood) || 5)}`}>{avgMood}/10</p></div><Heart className="w-8 h-8 text-muted-foreground" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t("progress.moodEntries")}</p><p className="text-3xl font-semibold">{moodEntries.length}</p></div><TrendingUp className="w-8 h-8 text-muted-foreground" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t("progress.exercisesCompleted")}</p><p className="text-3xl font-semibold">{completedAssignments}</p></div><BookOpen className="w-8 h-8 text-muted-foreground" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{t("progress.emotionsProcessed")}</p><p className="text-3xl font-semibold">{exportData?.emotion_registers_count || 0}</p></div><Brain className="w-8 h-8 text-muted-foreground" /></div></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> {t("progress.moodEvolution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {moodAnalytics?.chart_data ? (
              <MoodChart data={moodAnalytics.chart_data} averageMood={moodAnalytics.average_mood} />
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">{t("progress.notEnoughData")}</div>
            )}
          </CardContent>
        </Card>

        {moodAnalytics?.top_emotions && moodAnalytics.top_emotions.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Star className="w-5 h-5" /> {t("progress.topEmotions")}</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {moodAnalytics.top_emotions.map(([emotion, count], idx) => (
                  <div key={idx} className="px-4 py-2 bg-secondary rounded-full flex items-center gap-2">
                    <span className="font-medium">{emotion}</span>
                    <span className="text-sm text-muted-foreground">({count}x)</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full max-w-full justify-start overflow-x-auto h-auto flex-nowrap scrollbar-none" data-testid="patient-tabs-list">
            <TabsTrigger value="mood" className="gap-2"><Heart className="w-4 h-4" /> {t("progress.mood")}</TabsTrigger>
            <TabsTrigger value="thoughts" className="gap-2"><Brain className="w-4 h-4" /> {t("progress.thoughts")}</TabsTrigger>
            <TabsTrigger value="gratitude" className="gap-2"><Star className="w-4 h-4" /> {t("progress.gratitude")}</TabsTrigger>
            <TabsTrigger value="assignments" className="gap-2"><BookOpen className="w-4 h-4" /> {t("progress.exercises")}</TabsTrigger>
            <TabsTrigger value="reports" className="gap-2" data-testid="tab-patient-reports"><ClipboardList className="w-4 h-4" /> Séances {sessionReports.length > 0 && <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{sessionReports.length}</span>}</TabsTrigger>
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-patient-profile"><User className="w-4 h-4" /> Fiche patient</TabsTrigger>
            <TabsTrigger value="anamnesis" className="gap-2" data-testid="tab-patient-anamnesis"><FolderOpen className="w-4 h-4" /> Dossier</TabsTrigger>
            <TabsTrigger value="synthesis" className="gap-2" data-testid="tab-patient-synthesis"><FileText className="w-4 h-4" /> Synthèse</TabsTrigger>
            <TabsTrigger value="sent" className="gap-2" data-testid="tab-patient-sent"><Send className="w-4 h-4" /> Envois</TabsTrigger>
            {badgeData && <TabsTrigger value="badges" className="gap-2" data-testid="tab-patient-badges"><Trophy className="w-4 h-4" /> Badges {badgeData.total_earned > 0 && <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{badgeData.total_earned}</span>}</TabsTrigger>}
          </TabsList>

          <TabsContent value="mood" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-xl">{t("progress.moodHistoryTitle")}</CardTitle></CardHeader>
              <CardContent>
                {moodEntries.length > 0 ? (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {moodEntries.map((entry) => (
                      <div key={entry.entry_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xl font-semibold ${getMoodColor(entry.mood_level)}`}>{entry.mood_level}/10</span>
                          <span className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {entry.emotions.map((e, idx) => <span key={idx} className="text-xs px-2 py-1 bg-muted rounded-full">{e}</span>)}
                        </div>
                        {entry.notes && <p className="text-sm text-muted-foreground">{entry.notes}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Heart className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("progress.noMoodEntries")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="thoughts" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-xl">{t("progress.thoughtRecordsTitle")}</CardTitle></CardHeader>
              <CardContent>
                {exportData?.emotion_registers && exportData.emotion_registers.length > 0 ? (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {exportData.emotion_registers.map((record, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-muted-foreground">{formatDate(record.created_at)}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-rose-500 text-sm">{record.emotion_intensity}/10</span>
                            <span className="text-muted-foreground">&rarr;</span>
                            <span className="text-emerald-500 text-sm">{record.new_emotion_intensity}/10</span>
                          </div>
                        </div>
                        <p className="font-medium mb-2">{t("progress.situation")}: {record.situation}</p>
                        <p className="text-sm text-muted-foreground mb-2"><strong>{t("progress.thought")}:</strong> {record.automatic_thoughts}</p>
                        <p className="text-sm text-emerald-700 bg-emerald-50 p-2 rounded"><strong>{t("progress.balancedThoughtLabel")}:</strong> {record.balanced_thought}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("progress.noEmotionRegisters")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="gratitude" className="mt-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-xl">{t("progress.gratitudeJournal")}</CardTitle></CardHeader>
              <CardContent>
                {exportData?.gratitude_entries && exportData.gratitude_entries.length > 0 ? (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {exportData.gratitude_entries.map((entry, idx) => (
                      <div key={idx} className="p-4 border rounded-lg bg-amber-50/50">
                        <p className="text-sm text-amber-700 mb-3">{formatDate(entry.created_at)}</p>
                        <ul className="space-y-1">
                          {entry.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm"><Star className="w-4 h-4 text-amber-500 mt-0.5" />{item}</li>
                          ))}
                        </ul>
                        {entry.reflection && <p className="mt-3 pt-3 border-t border-amber-200 text-sm italic text-muted-foreground">"{entry.reflection}"</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Star className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("progress.noGratitude")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="assignments" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl">{t("progress.assignedExercisesTitle")}</CardTitle>
                <Link to="/therapist/exercises">
                  <Button size="sm" data-testid="assign-exercise-btn">
                    <Plus className="w-4 h-4 mr-1" /> Assigner un exercice
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {assignments.length > 0 ? (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {assignments.map((assignment) => (
                      <div key={assignment.assignment_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{assignment.title}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs ${assignment.status === "completed" ? "bg-accent/10 text-accent" : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"}`}>
                            {assignment.status === "completed" ? t("exercise.completed") : t("exercise.pending")}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground capitalize">{(assignment.exercise_type || '').replace(/_/g, " ")}</p>
                        {assignment.instructions && <p className="text-sm text-muted-foreground mt-2">{assignment.instructions}</p>}
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => downloadWorksheetPdf(assignment.exercise_type, { patient_id: patientId }, `fiche-${assignment.exercise_type}-${patient?.first_name || "patient"}.pdf`).catch(() => toast.error("PDF indisponible"))} title="Fiche papier dans la langue et pour l'âge du patient" data-testid={`assignment-worksheet-pdf-${assignment.assignment_id}`}>
                            <FileDown className="w-3 h-3" /> PDF papier
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setPersonalize({ type: assignment.exercise_type, title: assignment.title })} title="Adapter les exemples grisés à la situation du patient avant impression" data-testid={`assignment-worksheet-custom-${assignment.assignment_id}`}>
                            <Wand2 className="w-3 h-3" /> Personnaliser
                          </Button>
                          {customSheets.includes(assignment.exercise_type) && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`assignment-custom-badge-${assignment.assignment_id}`}>fiche personnalisée</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{t("progress.noAssignments")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            <PatientSessionsWorkspace patientId={patientId} patient={patient} user={user} sessions={sessionReports} setSessions={setSessionReports} onOpenAnamnesis={() => setActiveTab("anamnesis")} />
          </TabsContent>

          <TabsContent value="sent" className="mt-6">
            <SentDocumentsPanel patientId={patientId} onOpenSession={(id) => { setSearchParams({ session: id }, { replace: true }); setActiveTab("reports"); }} />
          </TabsContent>


          <TabsContent value="profile" className="mt-6">
            <Card data-testid="patient-profile-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><User className="w-5 h-5" /> Fiche patient</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="pf-name">Nom complet (affiche)</Label>
                    <Input id="pf-name" data-testid="pf-name" value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} placeholder="Nom affiche" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pf-first-name">Prenom</Label>
                    <Input id="pf-first-name" data-testid="pf-first-name" value={profileForm.first_name} onChange={e => setProfileForm(p => ({...p, first_name: e.target.value}))} placeholder="Prenom" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pf-last-name">Nom de famille</Label>
                    <Input id="pf-last-name" data-testid="pf-last-name" value={profileForm.last_name} onChange={e => setProfileForm(p => ({...p, last_name: e.target.value}))} placeholder="Nom de famille" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pf-dob">Date de naissance</Label>
                    <Input id="pf-dob" data-testid="pf-dob" type="date" value={profileForm.date_of_birth} onChange={e => setProfileForm(p => ({...p, date_of_birth: e.target.value}))} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pf-gender">Genre</Label>
                    <Select value={profileForm.gender} onValueChange={v => setProfileForm(p => ({...p, gender: v}))}>
                      <SelectTrigger id="pf-gender" data-testid="pf-gender" className="mt-1">
                        <SelectValue placeholder="Selectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Homme</SelectItem>
                        <SelectItem value="F">Femme</SelectItem>
                        <SelectItem value="NB">Non-binaire</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pf-phone">Telephone</Label>
                    <Input id="pf-phone" data-testid="pf-phone" type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({...p, phone: e.target.value}))} placeholder="06 12 34 56 78" className="mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pf-parent-emails">E-mail(s) des parents <span className="text-muted-foreground text-xs">(mineurs — guidance parentale, séparés par des virgules)</span></Label>
                    <Input id="pf-parent-emails" data-testid="pf-parent-emails" value={profileForm.parent_emails} onChange={e => setProfileForm(p => ({...p, parent_emails: e.target.value}))} placeholder="parent1@exemple.fr, parent2@exemple.fr" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pf-address">Adresse postale</Label>
                    <Input id="pf-address" data-testid="pf-address" value={profileForm.address} onChange={e => setProfileForm(p => ({...p, address: e.target.value}))} placeholder="12 rue de Paris, 75001 Paris" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="pf-language">Langue du patient (interface, e-mails, documents, IA)</Label>
                    <Select value={profileForm.language} onValueChange={v => setProfileForm(p => ({...p, language: v}))}>
                      <SelectTrigger id="pf-language" data-testid="pf-language" className="mt-1"><SelectValue placeholder="Français" /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`pf-language-${l.id}`}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pf-notes">Notes internes (non visibles par le patient)</Label>
                    <textarea id="pf-notes" data-testid="pf-notes" value={profileForm.notes} onChange={e => setProfileForm(p => ({...p, notes: e.target.value}))} rows={3} placeholder="Contexte, points de vigilance..." className="mt-1 w-full px-3 py-2 text-sm border rounded-lg bg-background border-border" />
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="pf-save-btn" className="gap-2">
                    <Save className="w-4 h-4" /> {savingProfile ? "Enregistrement..." : "Enregistrer la fiche"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="anamnesis" className="mt-6">
            <PatientAnamnesis patientId={patientId} />
          </TabsContent>

          <TabsContent value="synthesis" className="mt-6">
            <ClinicalSynthesis patientId={patientId} reportsCount={sessionReports.length} />
          </TabsContent>
          {badgeData && (
            <TabsContent value="badges" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Badges ({badgeData.total_earned}/{badgeData.total_available})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {badgeData.badges?.map(badge => (
                      <div
                        key={badge.badge_id}
                        className={`flex flex-col items-center p-3 rounded-xl border-2 ${
                          badge.earned
                            ? "border-amber-300 bg-amber-50/50 dark:bg-amber-950/20"
                            : "border-dashed border-muted opacity-50"
                        }`}
                        data-testid={`therapist-badge-${badge.badge_id}`}
                      >
                        <div className={`w-16 h-16 rounded-full overflow-hidden mb-2 ${!badge.earned ? "grayscale" : ""}`}>
                          <img
                            src={{
                              premier_pas: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/803313d38d29855e6baa4d4d2f6f7fe2ed0b7b3f704ec014a415d303439d466d.png",
                              explorateur: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/ad0c99c9e7bc365003a9ef21e2ece0545cea5df445b9fa4b1486ff7dfae13846.png",
                              super_heros_tdah: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/2716935c61ed856bebaca22dbeef649c87121c63f650b24585e90d7812b6999f.png",
                              gardien_jardin: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/4f0f630af0c0158d2fec374f43dd5430098a5f1c6fabdd860e3258d371abadae.png",
                              quiz_master: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/8941bfd1cb53d8c6bd46bb9faae3444be8e89ddfdb90a7387dbbbb5471fda565.png",
                              regulier: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/5edf41cf411d72332946ab60ca68530c4036dd45bb9324fe7d4b8b0d889001f2.png",
                              champion: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/40147c6bacfade03af2ed2931d1699bd0b4dac949e28638e087fe5b62417ae1d.png",
                              collectionneur: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/88d539e894fdaba96c07d2f21d408e75d1565701603a554155146717e66e5ebc.png",
                              genereux: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/29214dc0b68aaf250859a4b4026976c892ba11d45994eddb48035bbf3576a21d.png",
                            }[badge.badge_id]}
                            alt={badge.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-xs font-bold text-center">{badge.name}</span>
                        <span className="text-[10px] text-muted-foreground text-center mt-0.5">{badge.description}</span>
                        {badge.earned && badge.earned_at && (
                          <span className="text-[10px] text-amber-600 mt-1">
                            {new Date(badge.earned_at).toLocaleDateString(locale === "fr" ? "fr-FR" : locale === "es" ? "es-ES" : "en-US")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
        <MergePatientDialog open={mergeOpen} onOpenChange={setMergeOpen} patientId={patientId} onMerged={() => { onPatientChanged?.(); window.location.reload(); }} />
        <WorksheetPersonalizeDialog open={!!personalize} onOpenChange={(o) => { if (!o) setPersonalize(null); }} exerciseType={personalize?.type} exerciseTitle={personalize?.title} patientId={patientId} patientName={patient?.first_name || (patient?.name || "").split(" ")[0]} onSaved={loadCustomSheets} />

        <div className="hidden print:block mt-8 pt-4 border-t text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Leaf className="w-4 h-4" />
            <span className="font-heading font-semibold">Kintsugi TCC</span>
          </div>
          <p>{t("progress.reportGenerated")} {new Date().toLocaleDateString(locale === "fr" ? "fr-FR" : locale === "es" ? "es-ES" : "en-US", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
      </div>
  );
};

export default PatientProgressPage;
